import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { CardPay, FeeLine, SaveCardBox } from "@/components/CardPay";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/landing/Logo";
import { TipPicker } from "@/components/TipPicker";
import { fmt12 } from "@/lib/time";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 text-sm py-2 border-b border-white/5 last:border-0">
    <span className="text-gray-500">{k}</span>
    <span className="text-white text-right">{v}</span>
  </div>
);

export default function PayPage() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();
  const t = params.get("t") || "";
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [save, setSave] = useState(true);
  const [done, setDone] = useState(false);
  const [tip, setTip] = useState(0);

  useEffect(() => {
    api.get(`/pay/${bookingId}`, { params: { t } }).then((r) => setInfo(r.data)).catch(() => setError("This payment link is invalid or has expired."));
  }, [bookingId, t]);

  const pay = async (sourceId, verificationToken) => {
    setPaying(true);
    try {
      await api.post(`/pay/${bookingId}`, { t, source_id: sourceId, verification_token: verificationToken, tip, save_card: save });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  const paid = done || info?.paid;
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-3xl border border-sky-500/20 bg-[#0d0d0d] p-6 sm:p-8 space-y-6" data-testid="pay-page">
        <div className="flex justify-center"><Logo size={48} showWordmark /></div>
        {error ? (
          <p className="text-center text-red-300" data-testid="pay-error">{error}</p>
        ) : !info ? (
          <Loader2 className="w-6 h-6 animate-spin text-sky-400 mx-auto" />
        ) : paid ? (
          <div className="text-center space-y-3" data-testid="pay-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
            <h1 className="font-['Outfit'] text-2xl">You're confirmed!</h1>
            <p className="text-gray-400 text-sm">{info.scheduled_date} at {fmt12(info.scheduled_slot)}. A confirmation text is on its way. See you soon.</p>
          </div>
        ) : info.expired ? (
          <p className="text-center text-amber-300" data-testid="pay-expired">This booking hold has expired. Call (520) 380-4407 to rebook.</p>
        ) : (
          <>
            <div className="text-center">
              <p className="text-xs uppercase tracking-[0.3em] text-sky-400">Secure your appointment</p>
              <h1 className="font-['Outfit'] text-2xl mt-2">Hi {info.customer_name?.split(" ")[0] || "there"}, one last step</h1>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-4" data-testid="pay-summary">
              <Row k="When" v={`${info.scheduled_date} · ${fmt12(info.scheduled_slot)}`} />
              <Row k="Service" v={info.services.join(" + ")} />
              <Row k="Job total" v={`$${Number(info.total).toFixed(2)}`} />
              <Row k="Deposit due now" v={<span className="text-sky-400 font-semibold" data-testid="pay-deposit">${Number(info.deposit).toFixed(2)}</span>} />
            </div>
            <TipPicker base={Number(info.total)} tip={tip} setTip={setTip} />
            <FeeLine fee={info.card_fee || 0} pct={info.card_fee_pct} />
            <CardPay amount={Number(info.deposit) + (info.card_fee || 0) + tip} onToken={pay} busy={paying} buyerName={info.customer_name}
              label={`Pay $${(Number(info.deposit) + (info.card_fee || 0) + tip).toFixed(2)}${tip ? " (incl. tip)" : " deposit"}`} />
            <SaveCardBox checked={save} onChange={setSave} />
            <p className="text-[11px] text-gray-500 text-center">Your deposit is applied to your total.</p>
          </>
        )}
      </motion.div>
    </div>
  );
}
